// Finding status effects in a card's description.
//
// A description is plain text in data/cards.csv. Wherever it names a status effect (Stun, Frozen, Damage
// Boost…), that word is shown in a small box in the color of its token: on the placeholder card and in the
// card's close-up. Which words belong to which token is in src/config.ts under cardText.statusWords.
import { CONFIG } from '../config.ts';
import type { TokenDef } from '../shared/tokens.ts';
import { TOKEN_DB } from './data.ts';

/** A run of a description: plain words, or a status effect's name with the token it stands for. */
export interface TextPiece {
  text: string;
  token?: TokenDef;
}

let matcher: { re: RegExp; byWord: Map<string, TokenDef> } | null = null;
function statusMatcher() {
  if (matcher) return matcher;
  const byWord = new Map<string, TokenDef>();
  for (const [id, words] of Object.entries(CONFIG.cardText.statusWords)) {
    const token = TOKEN_DB.byId.get(id) ?? TOKEN_DB.list.find((d) => d.effect === id);
    if (token) for (const w of words) byWord.set(w, token);
  }
  // longest first, so "Damage Boost" is found before a shorter word could claim part of it
  const words = [...byWord.keys()].sort((a, b) => b.length - a.length).map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  matcher = { re: words.length ? new RegExp(`\\b(?:${words.join('|')})\\b`, 'g') : /$^/g, byWord };
  return matcher;
}

/** Splits a description into plain text and status-effect names (capital letters matter: "lock" is just a word). */
export function statusPieces(text: string): TextPiece[] {
  const { re, byWord } = statusMatcher();
  const out: TextPiece[] = [];
  let at = 0;
  re.lastIndex = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > at) out.push({ text: text.slice(at, m.index) });
    out.push({ text: m[0], token: byWord.get(m[0]) });
    at = m.index + m[0].length;
    if (m[0].length === 0) re.lastIndex++;
  }
  if (at < text.length) out.push({ text: text.slice(at) });
  return out;
}

/** How an Emperor ability's cost and cooldown read: "2 Gerzite · cooldown 2", "? Gerzite · cooldown 4", "Free · no cooldown". */
export function abilityTerms(a: { cost: number; dark: boolean; costVaries: boolean; cooldown: number }) {
  const coin = a.dark ? 'Dark Gerzite' : 'Gerzite';
  const cost = a.costVaries ? `? ${coin}` : a.cost > 0 ? `${a.cost} ${coin}` : 'Free';
  return `${cost} · ${a.cooldown > 0 ? `cooldown ${a.cooldown}` : 'no cooldown'}`;
}
